import { View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Image, Text } from "@src/components";
import type { VehicleLogShot } from "@visioni/a3s-vehicle-tracker";

/** The row shows this many thumbnails; the rest are counted in a "+N" badge. */
const MAX_SHOTS = 3;
const SHOT_WIDTH = 56;
const SHOT_HEIGHT = 36;

// A plain object, not a Unistyles style: `Image` hands its style to expo-image,
// which Unistyles doesn't process on web, so a stylesheet entry never applies.
const imageSize = { width: SHOT_WIDTH, height: SHOT_HEIGHT } as const;

interface CameraShotsProps {
  shots: readonly VehicleLogShot[];
}

/** Thumbnails of the vehicle from each camera that saw it. */
export function CameraShots({ shots }: CameraShotsProps) {
  if (shots.length === 0) {
    return (
      <View style={[styles.shot, styles.empty]}>
        <Text textStyle="xxs/regular" color="textMuted">
          No image
        </Text>
      </View>
    );
  }

  const visible = shots.slice(0, MAX_SHOTS);
  const hidden = shots.length - visible.length;

  return (
    <View style={styles.container}>
      {visible.map((shot, index) => (
        <View key={shot.key} style={styles.shot}>
          <Image source={{ uri: shot.imageUrl }} style={imageSize} contentFit="cover" />
          {hidden > 0 && index === visible.length - 1 && (
            <View style={styles.badge}>
              <Text textStyle="xxs/bold" style={styles.badgeText}>
                +{hidden}
              </Text>
            </View>
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    flexDirection: "row",
    gap: theme.spacing.xs,
  },
  shot: {
    width: SHOT_WIDTH,
    height: SHOT_HEIGHT,
    borderRadius: theme.spacing.xs,
    overflow: "hidden",
    backgroundColor: theme.colors.muted,
  },
  empty: {
    alignItems: "center",
    justifyContent: "center",
  },
  badge: {
    position: "absolute",
    right: 2,
    bottom: 2,
    paddingHorizontal: theme.spacing.xs,
    borderRadius: theme.spacing.xs,
    backgroundColor: theme.colors.interactive.buttons.default_background,
  },
  badgeText: {
    color: theme.colors.interactive.buttons.default_foreground,
  },
}));
